import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  FaExclamationTriangle, 
  FaExclamationCircle,
  FaHospital,
  FaChartBar,
  FaTools,
  FaComments,
  FaCalendarAlt,
  FaShieldAlt,
  FaLock,
  FaFileMedical,
  FaClipboardCheck,
  FaHeartbeat
} from 'react-icons/fa';
import { collection, query, where, getDocs, doc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
// Import decryption utility for PHI fields
import { decryptValue } from '../utils/encryption';
// Import date formatter for timezone-aware display
import { formatDateTime as formatDateTimeUtil } from '../utils/dateFormatter';
// Import organization utilities for multi-tenancy filtering
import { matchesOrganization } from '../utils/organization';

const MainScreen = ({ setStep, setPatientInfo, activePractice = '', organization = 'alliedoms', userRole = '' }) => {
  const [openProcedures, setOpenProcedures] = useState([]);
  const [dashboardCounts, setDashboardCounts] = useState({
    complications: 0,
    emergency: 0,
    operations: 0,
    security: 0
  });
  // userRole is now passed as a prop from App.js to avoid race conditions
  
  // Operations subtypes for counting (memoized to prevent infinite re-renders)
  const operationsSubtypes = useMemo(() => [
    'Equipment___Surgical', 'Equipment___Imaging', 'Equipment___Sterilization', 
    'Equipment___Anesthesia', 'Equipment___Computer_Software', 'Equipment___Facility_Systems',
    'Complaint___Clinical_Care', 'Complaint___Customer_Service', 
    'Complaint___Billing_Financial', 'Complaint___Facility_Environment',
    'Scheduling___Appointment', 'Scheduling___Communication', 
    'Scheduling___Documentation', 'Scheduling___System_Process'
  ], []);
  
  // IT Breach subtypes for counting (memoized)
  const itBreachSubtypes = useMemo(() => [
    'DataBreach', 'RansomwareAttack', 'CyberAttack', 
    'SystemBreach', 'EmailBreach', 'DeviceBreach'
  ], []);
  
  // HIPAA subtypes for counting (memoized)
  const hipaaSubtypes = useMemo(() => [
    'UnauthorizedDisclosure', 'ImproperAccess', 'LostRecords', 
    'VendorBreach', 'PatientRights', 'TransmissionError'
  ], []);
  
  // Helper to filter data by organization using smart inference for legacy documents
  const filterByOrganization = useCallback((data) => {
    return data.filter(item => matchesOrganization(item, organization));
  }, [organization]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch dashboard counts once on mount (filtered client-side by organization)
  useEffect(() => {
    if (!activePractice && userRole !== 'System Administrator') return;

    const shouldFilterByPractice = userRole !== 'System Administrator' && activePractice;
    // Always filter client-side by organization to handle legacy data without organization field.
    // Server-side count would include all documents regardless of organization.

    // Helper to get count - always filters client-side by organization
    const getCount = async (q) => {
      const snap = await getDocs(q);
      return snap.docs.filter(d => matchesOrganization(d.data(), organization)).length;
    };

    // Fetch all counts in parallel
    const fetchAllCounts = async () => {
      try {
        // Build all promises
        const complicationSubtypes = ['Anesthesia', 'Dental Implant', 'Dentoalveolar Surgery', 'Needle Stick Injury'];
        
        const allPromises = [];
        const labels = [];

        // Complications
        for (const subtype of complicationSubtypes) {
          const formRefDoc = doc(db, 'Complications', 'formRefDoc');
          const subCollection = collection(formRefDoc, subtype);
          const q = shouldFilterByPractice 
            ? query(subCollection, where("practice", "==", activePractice))
            : subCollection;
          allPromises.push(getCount(q));
          labels.push('complication');
        }

        // Emergency
        const emergencyCollection = collection(db, 'Emergency');
        const emergencyQ = shouldFilterByPractice 
          ? query(emergencyCollection, where("practice", "==", activePractice))
          : emergencyCollection;
        allPromises.push(getCount(emergencyQ));
        labels.push('emergency');

        // Operations
        for (const subtype of operationsSubtypes) {
          const parentRef = doc(db, 'Operations', 'opsRefDoc');
          const subCollection = collection(parentRef, subtype);
          const q = shouldFilterByPractice 
            ? query(subCollection, where("practice", "==", activePractice))
            : subCollection;
          allPromises.push(getCount(q));
          labels.push('operations');
        }

        // Aggressive Patient
        const aggressiveRef = doc(db, 'AggressivePatientInteractions', 'root');
        const aggressiveCollection = collection(aggressiveRef, 'Entries');
        const aggressiveQ = shouldFilterByPractice 
          ? query(aggressiveCollection, where("practice", "==", activePractice))
          : aggressiveCollection;
        allPromises.push(getCount(aggressiveQ));
        labels.push('security');

        // IT Breach
        for (const subtype of itBreachSubtypes) {
          const parentRef = doc(db, 'ITBreach', 'root');
          const subCollection = collection(parentRef, subtype);
          const q = shouldFilterByPractice 
            ? query(subCollection, where("practice", "==", activePractice))
            : subCollection;
          allPromises.push(getCount(q));
          labels.push('security');
        }

        // HIPAA
        for (const subtype of hipaaSubtypes) {
          const parentRef = doc(db, 'HIPAADisclosure', 'root');
          const subCollection = collection(parentRef, subtype);
          const q = shouldFilterByPractice 
            ? query(subCollection, where("practice", "==", activePractice))
            : subCollection;
          allPromises.push(getCount(q));
          labels.push('security');
        }

        // Execute all in parallel
        const results = await Promise.all(allPromises.map(p => p.catch(() => 0)));

        // Aggregate
        let complications = 0, emergency = 0, operations = 0, security = 0;
        results.forEach((count, idx) => {
          const label = labels[idx];
          if (label === 'complication') complications += count;
          else if (label === 'emergency') emergency += count;
          else if (label === 'operations') operations += count;
          else if (label === 'security') security += count;
        });

        setDashboardCounts({ complications, emergency, operations, security });

      } catch (error) {
        console.error('Error fetching dashboard counts:', error);
      }
    };

    fetchAllCounts();
  }, [activePractice, userRole, organization, operationsSubtypes, itBreachSubtypes, hipaaSubtypes]);

  const handleMarkAs = useCallback((procedure, outcomeType) => {
    // Set patient info with procedure data
    setPatientInfo({
      patientID: procedure.patientID || '',
      procedureId: procedure.procedureId || null,
      procedureType: procedure.procedureType || null
    });

    // Navigate to appropriate workflow
    if (outcomeType === 'success') {
      setStep('successPatientInfo');
    } else if (outcomeType === 'complication') {
      // Go to complications menu - patient info already attached
      setStep('complicationsMenuFromProcedure');
    } else if (outcomeType === 'emergency') {
      // Go directly to emergency form - patient info already attached
      setStep('emergencyCheck');
    }
  }, [setPatientInfo, setStep]);

  const getProcedureTypeName = (procedureType) => {
    const procedureNames = {
      'extraction': 'Tooth Extraction',
      'implant': 'Dental Implant',
      'biopsy': 'Oral Biopsy',
      'cyst': 'Cyst Removal',
      'fracture': 'Fracture Repair',
      'orthognathic': 'Orthognathic Surgery',
      'tmj': 'TMJ Surgery',
      'reconstruction': 'Facial Reconstruction',
      'other': 'Other Procedure'
    };
    return procedureNames[procedureType] || procedureType || 'Unknown';
  };

  // Use timezone-aware date formatter
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const formatted = formatDateTimeUtil(timestamp, { includeTime: true, dateStyle: 'medium' });
    return formatted === '—' ? 'N/A' : formatted;
  };

  const goToOutcomes = () => setStep('viewData');

  // Action button configuration with new color scheme
  const actionButtons = [
    {
      id: 'clinical',
      title: 'Clinical Outcomes',
      icon: FaHeartbeat,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      items: [
        {
          label: 'Complication',
          icon: FaExclamationTriangle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          hoverBg: 'hover:bg-red-100',
          hoverBorder: 'hover:border-red-300',
          onClick: () => setStep('complicationsMenu')
        },
        {
          label: 'Emergency',
          icon: FaExclamationCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          hoverBg: 'hover:bg-red-100',
          hoverBorder: 'hover:border-red-300',
          onClick: () => setStep('emergencyPatientInfo')
        }
      ]
    },
    {
      id: 'operations',
      title: 'Operations & Quality',
      icon: FaClipboardCheck,
      iconColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      items: [
        {
          label: 'Equipment Malfunction',
          icon: FaTools,
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50',
          borderColor: 'border-emerald-200',
          hoverBg: 'hover:bg-emerald-100',
          hoverBorder: 'hover:border-emerald-300',
          onClick: () => setStep('operationsMenu')
        },
        {
          label: 'Patient Complaint',
          icon: FaComments,
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50',
          borderColor: 'border-emerald-200',
          hoverBg: 'hover:bg-emerald-100',
          hoverBorder: 'hover:border-emerald-300',
          onClick: () => setStep('complaintsMenu')
        },
        {
          label: 'Scheduling or Communication Error',
          icon: FaCalendarAlt,
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50',
          borderColor: 'border-emerald-200',
          hoverBg: 'hover:bg-emerald-100',
          hoverBorder: 'hover:border-emerald-300',
          onClick: () => setStep('schedulingMenu')
        }
      ]
    },
    {
      id: 'safety',
      title: 'Safety & Security',
      icon: FaShieldAlt,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      items: [
        {
          label: 'Aggressive Patient Interaction',
          icon: FaShieldAlt,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          hoverBg: 'hover:bg-blue-100',
          hoverBorder: 'hover:border-blue-300',
          onClick: () => setStep('aggressivePatient')
        },
        {
          label: 'IT Security Event',
          icon: FaLock,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          hoverBg: 'hover:bg-blue-100',
          hoverBorder: 'hover:border-blue-300',
          onClick: () => setStep('itBreachMenu')
        },
        {
          label: 'HIPAA Disclosure',
          icon: FaFileMedical,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          hoverBg: 'hover:bg-blue-100',
          hoverBorder: 'hover:border-blue-300',
          onClick: () => setStep('hipaaMenu')
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-4 sm:py-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Action Sections */}
        <div className="space-y-4">
          {actionButtons.map((section) => (
            <div
              key={section.id}
              className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-gray-900/5 transition-shadow hover:shadow-lg"
            >
              {/* Section Header */}
              <div className={`px-4 py-3 ${section.bgColor} sm:px-4`}>
                <div className="flex items-center gap-2">
                  <div className={`flex size-8 items-center justify-center rounded-lg ${section.bgColor} ring-1 ring-inset ${section.borderColor}`}>
                    <section.icon className={`size-4 ${section.iconColor}`} aria-hidden="true" />
                  </div>
                  <h2 className="text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">
                    {section.title}
                  </h2>
                </div>
              </div>

              {/* Action Buttons Grid */}
              <div className="px-4 py-4 sm:px-4">
                <dl className={`grid grid-cols-1 gap-3 ${section.id === 'clinical' ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} sm:gap-4`}>
                  {section.items.map((item) => (
                    <button
                      key={item.label}
                      onClick={item.onClick}
                      className={`group relative flex min-h-[80px] items-center justify-center gap-3 rounded-lg border-2 ${item.borderColor} ${item.bgColor} px-4 py-4 text-center transition-all duration-200 ${item.hoverBg} ${item.hoverBorder} outline-none`}
                    >
                      <div className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${item.bgColor} transition-transform group-hover:scale-110`}>
                        <item.icon className={`size-5 ${item.color}`} aria-hidden="true" />
                      </div>
                      <dt className="text-sm font-semibold text-gray-900 sm:text-base">
                        {item.label}
                      </dt>
                    </button>
                  ))}
                </dl>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions Section */}
        <div className="mt-6">
          {/* BEGIN PROCEDURE SECTION - COMMENTED OUT FOR FUTURE USE
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
            <div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-gray-900/5 transition-shadow hover:shadow-lg">
              <div className="px-4 py-3 bg-teal-50 sm:px-4">
                <div className="flex items-center gap-2">
                  <div className="flex size-8 items-center justify-center rounded-lg bg-teal-50 ring-1 ring-inset border-teal-200">
                    <FaHospital className="size-4 text-teal-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold tracking-tight text-gray-900">
                    Begin Procedure
                  </h3>
                </div>
              </div>
              <div className="px-4 py-4 sm:px-4">
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => setStep('procedureSelection')}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                  >
                    <FaHospital className="size-4" />
                    Start New Procedure
                  </button>
                  <p className="text-center text-xs text-gray-600">
                    Select procedure type and begin patient tracking
                  </p>
                  
                  {openProcedures.length > 0 && (
                    <div className="mt-4 w-full">
                      <h4 className="mb-2 text-xs font-semibold text-gray-700">Open Procedures</h4>
                      <div className="overflow-hidden rounded-lg border border-gray-200">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                                  Patient ID
                                </th>
                                <th scope="col" className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                                  Type
                                </th>
                                <th scope="col" className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                                  Start Time
                                </th>
                                <th scope="col" className="px-2 py-1.5 text-center text-xs font-semibold text-gray-700">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {openProcedures.slice(0, 5).map((proc) => (
                                <tr key={proc.id} className="hover:bg-gray-50">
                                  <td className="whitespace-nowrap px-2 py-1.5 text-xs text-gray-900">
                                    {proc.patientID || 'N/A'}
                                  </td>
                                  <td className="whitespace-nowrap px-2 py-1.5 text-xs text-gray-600">
                                    {getProcedureTypeName(proc.procedureType)}
                                  </td>
                                  <td className="whitespace-nowrap px-2 py-1.5 text-xs text-gray-600">
                                    {formatDateTime(proc.startTime)}
                                  </td>
                                  <td className="whitespace-nowrap px-2 py-1.5 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <button
                                        onClick={() => handleMarkAs(proc, 'complication')}
                                        className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 hover:bg-red-100 transition-colors"
                                        title="Mark as Complication"
                                      >
                                        <FaExclamationTriangle className="size-3" />
                                      </button>
                                      <button
                                        onClick={() => handleMarkAs(proc, 'emergency')}
                                        className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 hover:bg-red-100 transition-colors"
                                        title="Mark as Emergency"
                                      >
                                        <FaExclamationCircle className="size-3" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {openProcedures.length > 5 && (
                          <div className="bg-gray-50 px-2 py-1 text-center text-[10px] text-gray-500">
                            Showing 5 of {openProcedures.length} procedures
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          END BEGIN PROCEDURE SECTION */}

          {/* Dashboard Preview Card - Now Full Width */}
          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-gray-900/5 transition-shadow hover:shadow-lg">
            <div className="px-4 py-3 bg-red-50 sm:px-6">
              <div className="flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-red-50 ring-1 ring-inset border-red-200">
                  <FaChartBar className="size-4 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold tracking-tight text-gray-900">
                  Dashboard Overview
                </h3>
              </div>
            </div>
            <div className="px-4 py-6 sm:px-6">
              {/* Dashboard Stats - Expanded */}
              <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="rounded-lg bg-red-50 px-4 py-4 text-center ring-1 ring-red-100">
                  <div className="text-3xl font-bold text-red-700">{dashboardCounts.complications}</div>
                  <div className="mt-1 text-sm font-medium text-red-600">Complications</div>
                </div>
                <div className="rounded-lg bg-red-50 px-4 py-4 text-center ring-1 ring-red-100">
                  <div className="text-3xl font-bold text-red-700">{dashboardCounts.emergency}</div>
                  <div className="mt-1 text-sm font-medium text-red-600">Emergency</div>
                </div>
                <div className="rounded-lg bg-emerald-50 px-4 py-4 text-center ring-1 ring-emerald-100">
                  <div className="text-3xl font-bold text-emerald-700">{dashboardCounts.operations}</div>
                  <div className="mt-1 text-sm font-medium text-emerald-600">Operations</div>
                </div>
                <div className="rounded-lg bg-blue-50 px-4 py-4 text-center ring-1 ring-blue-100">
                  <div className="text-3xl font-bold text-blue-700">{dashboardCounts.security}</div>
                  <div className="mt-1 text-sm font-medium text-blue-600">Security</div>
                </div>
              </div>
              
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                <button
                  onClick={goToOutcomes}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  <FaChartBar className="size-4" />
                  View Full Dashboard
                </button>
                <p className="text-center text-sm text-gray-600">
                  Quick overview of recent outcomes and metrics
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainScreen;